import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { ClipboardCheck, CheckCircle2, AlertTriangle, Clock, User, Plus, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  current: { label: 'Current', color: 'bg-[#A8E6CF]/10 text-[#A8E6CF]', icon: CheckCircle2 },
  due_soon: { label: 'Due Soon', color: 'bg-amber-500/10 text-amber-400', icon: Clock },
  expired: { label: 'Expired', color: 'bg-red-500/10 text-red-400', icon: AlertTriangle },
  not_started: { label: 'Not Started', color: 'bg-muted text-muted-foreground', icon: X },
};

export default function TrainingMatrix() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recordDialog, setRecordDialog] = useState(false);
  const [recordForm, setRecordForm] = useState({ caregiver_id: '', training_id: '', completed_at: '', notes: '' });
  const [certFile, setCertFile] = useState(null);

  useEffect(() => { fetchMatrix(); }, []);

  const fetchMatrix = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/training-matrix`, { withCredentials: true });
      setData(data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const recordCompletion = async () => {
    try {
      if (certFile) {
        // Upload with certificate
        const fd = new FormData();
        fd.append('file', certFile);
        fd.append('caregiver_id', recordForm.caregiver_id);
        fd.append('training_id', recordForm.training_id);
        fd.append('completed_at', recordForm.completed_at);
        fd.append('notes', recordForm.notes);
        await axios.post(`${API}/api/training-matrix/record-with-cert`, fd, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/training-matrix/record`, recordForm, { withCredentials: true });
      }
      toast.success('Training recorded');
      setRecordDialog(false);
      setRecordForm({ caregiver_id: '', training_id: '', completed_at: '', notes: '' });
      setCertFile(null);
      fetchMatrix();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Loading training matrix...</p></div></DashboardLayout>;

  const stats = data?.stats || {};
  const matrix = data?.matrix || [];
  const training = data?.mandatory_training || [];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="training-matrix-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-[#00A6A0]" />Training Matrix
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Mandatory training compliance per caregiver - {data?.region} region</p>
          </div>
          <Button size="sm" className="btn-primary" onClick={() => setRecordDialog(true)} data-testid="record-training-btn">
            <Plus className="w-4 h-4 mr-1" />Record Training
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="glass-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{stats.total_caregivers || 0}</p>
              <p className="text-xs text-muted-foreground">Caregivers</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-[#00A6A0]/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#00A6A0]">{stats.org_compliance_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Compliance Rate</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-[#A8E6CF]/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#A8E6CF]">{stats.total_completed || 0}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-red-500/30">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-400">{stats.total_overdue || 0}</p>
              <p className="text-xs text-muted-foreground">Overdue / Missing</p>
            </CardContent>
          </Card>
        </div>

        {/* Matrix Grid */}
        {matrix.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" />
            <p className="text-muted-foreground">No caregivers found</p>
            <p className="text-xs text-muted-foreground mt-1">Invite caregivers to see the training matrix</p>
          </div>
        ) : (
          <div className="space-y-4">
            {matrix.map(cg => (
              <Card key={cg.caregiver_id} className="glass-card border-border" data-testid={`cg-row-${cg.caregiver_id}`}>
                <CardContent className="p-4">
                  {/* Caregiver Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#00A6A0]/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-[#00A6A0]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{cg.caregiver_name}</p>
                        <p className="text-[10px] text-muted-foreground">{cg.caregiver_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`text-sm font-bold ${cg.compliance_rate >= 80 ? 'text-[#A8E6CF]' : cg.compliance_rate >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                        {cg.compliance_rate}%
                      </div>
                      <span className="text-[10px] text-muted-foreground">{cg.completed}/{cg.total}</span>
                    </div>
                  </div>

                  {/* Training Items Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
                    {cg.items.map(item => {
                      const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.not_started;
                      const Icon = cfg.icon;
                      return (
                        <div key={item.training_id} className={`rounded-lg px-2 py-1.5 ${cfg.color} text-[10px]`} title={`${item.name} - ${item.level}\n${item.completed_at ? `Completed: ${item.completed_at}` : 'Not completed'}\n${item.expires_at ? `Expires: ${item.expires_at}` : ''}`}
                          data-testid={`training-${cg.caregiver_id}-${item.training_id}`}>
                          <div className="flex items-center gap-1">
                            <Icon className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate font-medium">{item.name}</span>
                          </div>
                          {item.status === 'due_soon' && <p className="mt-0.5 opacity-80">{item.days_until_expiry}d left</p>}
                          {item.status === 'expired' && item.expires_at && <p className="mt-0.5 opacity-80">Expired {item.expires_at}</p>}
                          {item.status === 'current' && item.expires_at && <p className="mt-0.5 opacity-80">Until {item.expires_at}</p>}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Legend */}
        <div className="flex gap-4 items-center text-xs text-muted-foreground flex-wrap">
          {Object.entries(STATUS_CONFIG).map(([k, v]) => {
            const Icon = v.icon;
            return <span key={k} className="flex items-center gap-1"><span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded ${v.color}`}><Icon className="w-3 h-3" />{v.label}</span></span>;
          })}
        </div>
      </div>

      {/* Record Training Dialog */}
      <Dialog open={recordDialog} onOpenChange={setRecordDialog}>
        <DialogContent className="bg-background border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Record Training Completion</DialogTitle>
            <DialogDescription>Log a completed mandatory training for a caregiver.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Caregiver</label>
              <select value={recordForm.caregiver_id} onChange={e => setRecordForm(f => ({ ...f, caregiver_id: e.target.value }))}
                className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1" data-testid="record-caregiver">
                <option value="">Select caregiver...</option>
                {matrix.map(cg => <option key={cg.caregiver_id} value={cg.caregiver_id}>{cg.caregiver_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Training</label>
              <select value={recordForm.training_id} onChange={e => setRecordForm(f => ({ ...f, training_id: e.target.value }))}
                className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1" data-testid="record-training">
                <option value="">Select training...</option>
                {training.map(t => <option key={t.id} value={t.id}>{t.name} ({t.level})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Completion Date</label>
              <Input type="date" value={recordForm.completed_at} onChange={e => setRecordForm(f => ({ ...f, completed_at: e.target.value }))}
                className="bg-muted border-border text-foreground mt-1" data-testid="record-date" />
            </div>
            <Input value={recordForm.notes} onChange={e => setRecordForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes (optional)"
              className="bg-muted border-border text-foreground" />
            <div>
              <label className="text-xs text-muted-foreground">Training Certificate (PDF/Image)</label>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => setCertFile(e.target.files?.[0] || null)}
                className="w-full bg-muted border border-border rounded-md px-3 py-2 text-foreground text-sm mt-1 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-[#00A6A0]/10 file:text-[#00A6A0]"
                data-testid="cert-file-input" />
              {certFile && <p className="text-[10px] text-[#A8E6CF] mt-1">Selected: {certFile.name}</p>}
            </div>
            <Button className="w-full btn-primary" onClick={recordCompletion} disabled={!recordForm.caregiver_id || !recordForm.training_id || !recordForm.completed_at} data-testid="submit-record-btn">
              Record Completion
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
